import React, { useState, useEffect, useMemo } from 'react';
import { apiFetch } from '../../../shared/lib/apiClient';
import XLSX from 'xlsx-js-style';

/* ═══════════════════════════════════════════════════════════════════
   Document Detail — User-friendly document viewer
   Shows extracted data, line items, and enrichment in a clean layout
   ═══════════════════════════════════════════════════════════════════ */

const fmt = (v) => {
  if (v == null || v === '') return null;
  return '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const STATUS_COLORS = {
  complete: { bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500', label: 'Processed' },
  processing: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', label: 'Processing' },
  extracting: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', label: 'Extracting' },
  routing: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', label: 'Routing' },
  enriching: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-400', label: 'Enriching' },
  failed: { bg: 'bg-red-50', text: 'text-red-700', dot: 'bg-red-500', label: 'Error' },
  pending_review: { bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500', label: 'Needs Review' },
};

const DocumentDetailView = ({ loaded }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [pipeline, setPipeline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pipelineLoading, setPipelineLoading] = useState(false);
  const [error, setError] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showRawData, setShowRawData] = useState(false);
  const [lineItemSort, setLineItemSort] = useState({ key: null, dir: 'asc' });
  const [expandedRows, setExpandedRows] = useState({});

  // Load document list
  useEffect(() => {
    apiFetch('/api/documents')
      .then(async (res) => {
        const d = await res.json();
        const docs = (d.documents || []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setDocuments(docs);
        if (docs.length > 0) setSelectedDocId(docs[0].id);
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  // Load pipeline for selected doc
  useEffect(() => {
    if (!selectedDocId) return;
    setPipelineLoading(true);
    setPipeline(null);
    setShowRawData(false);
    apiFetch(`/api/documents/${selectedDocId}/pipeline`)
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load pipeline');
        return res.json();
      })
      .then(setPipeline)
      .catch((e) => setError(e.message))
      .finally(() => setPipelineLoading(false));
  }, [selectedDocId]);

  // Reset PDF when doc changes
  useEffect(() => { setPdfOpen(false); setPdfUrl(null); }, [selectedDocId]);

  const selectedDoc = documents.find((d) => d.id === selectedDocId);
  const doc = pipeline?.document;
  const bill = pipeline?.bill;
  const lineItems = pipeline?.line_items || [];
  const enrichedItems = pipeline?.enriched_items || [];
  const header = doc?.raw_extraction?.header || {};
  const classification = doc?.classification;

  // Build enrichment lookup
  const enrichmentMap = useMemo(() => {
    const map = {};
    enrichedItems.forEach((item) => {
      if (item.line_item_id) map[item.line_item_id] = item;
    });
    return map;
  }, [enrichedItems]);

  // Sorted line items
  const sortedLineItems = useMemo(() => {
    const items = [...lineItems];
    if (lineItemSort.key) {
      items.sort((a, b) => {
        let av = a[lineItemSort.key], bv = b[lineItemSort.key];
        if (typeof av === 'number' && typeof bv === 'number') return lineItemSort.dir === 'asc' ? av - bv : bv - av;
        av = String(av || ''); bv = String(bv || '');
        return lineItemSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
      });
    }
    return items;
  }, [lineItems, lineItemSort]);

  const handleSort = (key) => {
    setLineItemSort((prev) => ({
      key,
      dir: prev.key === key && prev.dir === 'asc' ? 'desc' : 'asc',
    }));
  };

  // Load PDF
  const openPdf = async () => {
    if (pdfOpen) { setPdfOpen(false); return; }
    if (!selectedDocId) return;
    setPdfLoading(true);
    try {
      const res = await apiFetch(`/api/documents/${selectedDocId}/view-url`);
      if (!res.ok) throw new Error('Failed to get PDF URL');
      const { url } = await res.json();
      setPdfUrl(url);
      setPdfOpen(true);
    } catch (e) {
      console.error('PDF load error:', e);
    } finally {
      setPdfLoading(false);
    }
  };

  // Key summary fields
  const vendorName = bill?.vendor_name || header.vendor_name || classification?.sender_name || '--';
  const docNumber = bill?.bill_number || header.document_number || header.invoice_number || '--';
  const docDate = bill?.bill_date || header.document_date || header.invoice_date;
  const dueDate = bill?.due_date || header.due_date;
  const totalAmount = bill?.total_amount || header.total_amount || header.total_due;
  const subtotal = header.subtotal;
  const salesTax = header.sales_tax;
  const paymentTerms = bill?.payment_terms || header.payment_terms;
  const poNumber = header.po_number;
  const status = STATUS_COLORS[selectedDoc?.processing_status] || STATUS_COLORS.complete;

  // Count enriched items with normalized pricing
  const normalizedCount = enrichedItems.filter((item) => {
    const pricing = item.enrichment?.pricing?.required || {};
    return pricing.price_per_unit && pricing.unit_normalized;
  }).length;

  // ── Export to branded Excel ──
  const exportToExcel = () => {
    const wb = XLSX.utils.book_new();

    // Arbcor brand palette
    const DARK = '18181B';    // zinc-900
    const ACCENT = '10B981';  // emerald-500
    const LIGHT_BG = 'F4F4F5'; // zinc-100
    const WHITE = 'FFFFFF';
    const BORDER = { style: 'thin', color: { rgb: 'D4D4D8' } };
    const allBorders = { top: BORDER, bottom: BORDER, left: BORDER, right: BORDER };

    const headerStyle = {
      font: { bold: true, color: { rgb: WHITE }, sz: 11, name: 'Calibri' },
      fill: { fgColor: { rgb: DARK } },
      alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
      border: allBorders,
    };
    const subHeaderStyle = {
      font: { bold: true, color: { rgb: DARK }, sz: 10, name: 'Calibri' },
      fill: { fgColor: { rgb: LIGHT_BG } },
      alignment: { vertical: 'center' },
      border: allBorders,
    };
    const cellStyle = {
      font: { sz: 10, name: 'Calibri', color: { rgb: '3F3F46' } },
      alignment: { vertical: 'center' },
      border: allBorders,
    };
    const moneyStyle = {
      ...cellStyle,
      numFmt: '$#,##0.00',
      alignment: { vertical: 'center', horizontal: 'right' },
    };
    const money4Style = {
      ...cellStyle,
      numFmt: '$#,##0.0000',
      alignment: { vertical: 'center', horizontal: 'right' },
    };
    const pctStyle = {
      ...cellStyle,
      numFmt: '0%',
      alignment: { vertical: 'center', horizontal: 'center' },
    };
    const accentStyle = {
      font: { bold: true, color: { rgb: ACCENT }, sz: 10, name: 'Calibri' },
      alignment: { vertical: 'center' },
      border: allBorders,
    };

    // Helper: apply header style to row 0 of a sheet
    const styleHeaders = (ws, colCount) => {
      for (let c = 0; c < colCount; c++) {
        const addr = XLSX.utils.encode_cell({ r: 0, c });
        if (ws[addr]) ws[addr].s = headerStyle;
      }
    };

    // Helper: style a data sheet with alternating rows
    const styleDataRows = (ws, rowCount, colCount, colFormats) => {
      for (let r = 1; r <= rowCount; r++) {
        const isAlt = r % 2 === 0;
        for (let c = 0; c < colCount; c++) {
          const addr = XLSX.utils.encode_cell({ r, c });
          if (!ws[addr]) ws[addr] = { v: '', t: 's' };
          const fmt = colFormats?.[c];
          const base = fmt === 'money' ? moneyStyle : fmt === 'money4' ? money4Style : fmt === 'pct' ? pctStyle : fmt === 'accent' ? accentStyle : cellStyle;
          ws[addr].s = {
            ...base,
            fill: { fgColor: { rgb: isAlt ? 'FAFAFA' : WHITE } },
          };
        }
      }
    };

    // ═══ Sheet 1: Summary ═══
    const now = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    const summaryData = [
      [{ v: 'ARBCOR', s: { font: { bold: true, sz: 18, color: { rgb: DARK }, name: 'Calibri' } } }, '', ''],
      [{ v: 'Invoice Enrichment Report', s: { font: { sz: 11, color: { rgb: '71717A' }, name: 'Calibri' } } }, '', ''],
      [{ v: `Generated ${now}`, s: { font: { sz: 9, color: { rgb: 'A1A1AA' }, name: 'Calibri', italic: true } } }, '', ''],
      ['', '', ''],
      [{ v: 'DOCUMENT DETAILS', s: { font: { bold: true, sz: 10, color: { rgb: ACCENT }, name: 'Calibri' } } }, '', ''],
      [{ v: 'Vendor', s: subHeaderStyle }, { v: vendorName, s: { ...cellStyle, font: { ...cellStyle.font, bold: true, sz: 12 } } }, ''],
      [{ v: 'Document #', s: subHeaderStyle }, { v: docNumber, s: cellStyle }, ''],
      [{ v: 'Date', s: subHeaderStyle }, { v: docDate || '', s: cellStyle }, ''],
      [{ v: 'Due Date', s: subHeaderStyle }, { v: dueDate || '', s: cellStyle }, ''],
      [{ v: 'Total Amount', s: subHeaderStyle }, { v: totalAmount ? Number(totalAmount) : '', t: 'n', s: { ...moneyStyle, font: { ...moneyStyle.font, bold: true, sz: 12 } } }, ''],
      [{ v: 'Payment Terms', s: subHeaderStyle }, { v: paymentTerms || '', s: cellStyle }, ''],
      ['', '', ''],
      [{ v: 'LINE ITEMS', s: { font: { bold: true, sz: 10, color: { rgb: ACCENT }, name: 'Calibri' } } }, '', ''],
      [{ v: 'Total Items', s: subHeaderStyle }, { v: lineItems.length, t: 'n', s: cellStyle }, ''],
      [{ v: 'Products', s: subHeaderStyle }, { v: enrichedItems.filter(i => i.enrichment?.identity?.required?.item_type === 'product').length, t: 'n', s: cellStyle }, ''],
      [{ v: 'Fees & Charges', s: subHeaderStyle }, { v: enrichedItems.filter(i => ['fee', 'tax', 'deposit'].includes(i.enrichment?.identity?.required?.item_type)).length, t: 'n', s: cellStyle }, ''],
    ];
    const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
    summarySheet['!cols'] = [{ wch: 20 }, { wch: 44 }, { wch: 10 }];
    summarySheet['!rows'] = [{ hpt: 28 }, { hpt: 18 }, { hpt: 14 }];
    XLSX.utils.book_append_sheet(wb, summarySheet, 'Summary');

    // ═══ Sheet 2: Line Items ═══
    const LI_COLS = [
      { key: 'line', label: '#', wch: 5 },
      { key: 'item_no', label: 'Item #', wch: 12 },
      { key: 'raw', label: 'Description (Invoice)', wch: 34 },
      { key: 'name', label: 'Product Name', wch: 38, fmt: 'accent' },
      { key: 'brand', label: 'Brand', wch: 18 },
      { key: 'category', label: 'Category', wch: 20 },
      { key: 'qty', label: 'Qty', wch: 8 },
      { key: 'unit_price', label: 'Unit Price', wch: 12, fmt: 'money' },
      { key: 'extended', label: 'Total', wch: 13, fmt: 'money' },
      { key: 'ppu', label: 'Price/Unit', wch: 13, fmt: 'money4' },
      { key: 'norm_unit', label: 'Unit', wch: 8 },
      { key: 'summary', label: 'What Was Purchased', wch: 52 },
    ];

    const liData = [LI_COLS.map(c => c.label)];
    for (const li of lineItems) {
      const enriched = enrichmentMap[li.id];
      const identity = enriched?.enrichment?.identity?.required || {};
      const pricing = enriched?.enrichment?.pricing?.required || {};

      liData.push([
        li.line_number || '',
        li.item_number || '',
        li.description || '',
        identity.standardized_name || '',
        identity.brand || '',
        [identity.category, identity.subcategory].filter(Boolean).join(' > ') || '',
        li.quantity_ordered ?? li.quantity_shipped ?? '',
        li.unit_price != null ? Number(li.unit_price) : '',
        li.extended_amount != null ? Number(li.extended_amount) : '',
        pricing.price_per_unit != null ? Number(pricing.price_per_unit) : '',
        pricing.unit_normalized || '',
        pricing.purchase_summary || '',
      ]);
    }

    // Totals row
    const extCol = 8; // Total column index
    liData.push([
      '', '', '', '', '', '', '', 'TOTAL',
      lineItems.reduce((sum, li) => sum + (Number(li.extended_amount) || 0), 0),
      '', '', '',
    ]);

    const liSheet = XLSX.utils.aoa_to_sheet(liData);
    liSheet['!cols'] = LI_COLS.map(c => ({ wch: c.wch }));
    liSheet['!rows'] = [{ hpt: 22 }];
    liSheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: LI_COLS.length - 1 } }) };
    styleHeaders(liSheet, LI_COLS.length);
    const colFormats = LI_COLS.map(c => c.fmt || null);
    styleDataRows(liSheet, liData.length - 1, LI_COLS.length, colFormats);

    // Style totals row
    const totalRowIdx = liData.length - 1;
    for (let c = 0; c < LI_COLS.length; c++) {
      const addr = XLSX.utils.encode_cell({ r: totalRowIdx, c });
      if (!liSheet[addr]) liSheet[addr] = { v: '', t: 's' };
      liSheet[addr].s = {
        font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: DARK } },
        fill: { fgColor: { rgb: LIGHT_BG } },
        border: { top: { style: 'medium', color: { rgb: DARK } }, bottom: { style: 'medium', color: { rgb: DARK } }, left: BORDER, right: BORDER },
        alignment: { vertical: 'center', horizontal: c === extCol ? 'right' : c === 5 ? 'right' : 'left' },
        numFmt: c === extCol ? '$#,##0.00' : undefined,
      };
    }

    XLSX.utils.book_append_sheet(wb, liSheet, 'Line Items');

    // ═══ Sheet 3: Price Breakdown ═══
    const PV_COLS = [
      { label: 'Product', wch: 38, fmt: 'accent' },
      { label: 'Price As', wch: 14 },
      { label: 'Price', wch: 13, fmt: 'money4' },
      { label: 'Primary', wch: 9 },
    ];

    const pvData = [PV_COLS.map(c => c.label)];
    for (const li of lineItems) {
      const enriched = enrichmentMap[li.id];
      const identity = enriched?.enrichment?.identity?.required || {};
      const pricing = enriched?.enrichment?.pricing?.required || {};
      for (const pv of (pricing.price_vector || [])) {
        pvData.push([
          identity.standardized_name || li.description || '',
          pv.label || '',
          pv.price != null ? Number(pv.price) : '',
          pv.is_primary ? 'YES' : '',
        ]);
      }
    }

    if (pvData.length > 1) {
      const pvSheet = XLSX.utils.aoa_to_sheet(pvData);
      pvSheet['!cols'] = PV_COLS.map(c => ({ wch: c.wch }));
      pvSheet['!rows'] = [{ hpt: 22 }];
      pvSheet['!autofilter'] = { ref: XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: 0, c: PV_COLS.length - 1 } }) };
      styleHeaders(pvSheet, PV_COLS.length);
      const pvFormats = PV_COLS.map(c => c.fmt || null);
      styleDataRows(pvSheet, pvData.length - 1, PV_COLS.length, pvFormats);

      // Highlight primary rows
      for (let r = 1; r < pvData.length; r++) {
        if (pvData[r][3] === 'YES') {
          const addr = XLSX.utils.encode_cell({ r, c: 3 });
          if (pvSheet[addr]) {
            pvSheet[addr].s = {
              font: { bold: true, sz: 10, name: 'Calibri', color: { rgb: WHITE } },
              fill: { fgColor: { rgb: ACCENT } },
              alignment: { vertical: 'center', horizontal: 'center' },
              border: allBorders,
            };
          }
        }
      }

      XLSX.utils.book_append_sheet(wb, pvSheet, 'Price Breakdown');
    }

    const fileName = `Arbcor_${vendorName.replace(/[^a-zA-Z0-9]/g, '_')}_${docNumber}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-zinc-400">
          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
          <span className="text-sm">Loading documents...</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 tracking-tight">Documents</h1>
          <p className="text-xs text-zinc-400 mt-0.5">View extracted data, line items, and enrichment results</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={selectedDocId || ''}
            onChange={(e) => setSelectedDocId(e.target.value)}
            className="px-3 py-1.5 text-sm border border-zinc-200 rounded-lg bg-white focus:outline-none focus:border-zinc-400 max-w-xs truncate"
          >
            {documents.length === 0 && <option value="">No documents</option>}
            {documents.map((d) => (
              <option key={d.id} value={d.id}>
                {d.extracted_data?.header?.vendor_name || d.file_name} — {d.extracted_data?.header?.document_number || d.extracted_data?.header?.invoice_number || d.id.slice(0, 8)}
              </option>
            ))}
          </select>
          {selectedDocId && (
            <button
              onClick={openPdf}
              disabled={pdfLoading}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                pdfOpen
                  ? 'bg-zinc-900 text-white border-zinc-900'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
              }`}
            >
              {pdfLoading ? (
                <svg className="animate-spin h-3.5 w-3.5" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
              ) : (
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M2 2h7l3 3v7a1 1 0 01-1 1H3a1 1 0 01-1-1V3a1 1 0 011-1z" />
                  <path d="M9 2v3h3" />
                </svg>
              )}
              {pdfOpen ? 'Hide PDF' : 'View PDF'}
            </button>
          )}
          {pipeline && lineItems.length > 0 && (
            <button
              onClick={exportToExcel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400 transition-colors"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M7 2v8M4 7l3 3 3-3" />
                <path d="M2 10v2h10v-2" />
              </svg>
              Export Excel
            </button>
          )}
        </div>
      </div>

      {/* Main layout */}
      <div className="flex gap-4">
        {/* Content column */}
        <div className={`min-w-0 transition-all duration-300 ${pdfOpen ? 'flex-1' : 'w-full'}`}>
          {pipelineLoading ? (
            <div className="flex items-center justify-center h-48">
              <div className="flex items-center gap-3 text-zinc-400">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" className="opacity-25" /><path d="M4 12a8 8 0 018-8" stroke="currentColor" strokeWidth="3" strokeLinecap="round" className="opacity-75" /></svg>
                <span className="text-sm">Loading document...</span>
              </div>
            </div>
          ) : pipeline ? (
            <div className="space-y-4">
              {/* ─── Document Summary Card ─── */}
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="px-5 py-4">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-zinc-900">{vendorName}</h2>
                      <p className="text-sm text-zinc-400 mt-0.5">
                        {classification?.document_type === 'invoice' ? 'Invoice' : classification?.document_type || 'Document'} #{docNumber}
                      </p>
                    </div>
                    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium ${status.bg} ${status.text}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${status.dot}`} />
                      {status.label}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
                    <SummaryField label="Total Amount" value={fmt(totalAmount)} large />
                    <SummaryField label="Date" value={fmtDate(docDate)} />
                    <SummaryField label="Due Date" value={fmtDate(dueDate)} />
                    <SummaryField label="Payment Terms" value={paymentTerms} />
                    {poNumber && <SummaryField label="PO Number" value={poNumber} />}
                    <SummaryField label="Line Items" value={lineItems.length || '--'} />
                  </div>
                </div>

                {/* Subtotal breakdown */}
                {(subtotal || salesTax) && (
                  <div className="border-t border-zinc-100 px-5 py-3 bg-zinc-50/50 flex items-center gap-6 text-xs">
                    {subtotal && <span className="text-zinc-500">Subtotal: <span className="font-medium text-zinc-700">{fmt(subtotal)}</span></span>}
                    {salesTax && <span className="text-zinc-500">Tax: <span className="font-medium text-zinc-700">{fmt(salesTax)}</span></span>}
                    {totalAmount && <span className="text-zinc-500 ml-auto">Total: <span className="font-semibold text-zinc-900 text-sm">{fmt(totalAmount)}</span></span>}
                  </div>
                )}
              </div>

              {/* ─── Line Items Table ─── */}
              {lineItems.length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
                    <h3 className="text-sm font-semibold text-zinc-900">Line Items</h3>
                    <span className="text-[11px] text-zinc-400">{lineItems.length} items</span>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="border-b border-zinc-100 bg-zinc-50/50">
                          <SortHeader label="#" sortKey="line_number" current={lineItemSort} onSort={handleSort} className="w-10 pl-5" />
                          <SortHeader label="Description" sortKey="description" current={lineItemSort} onSort={handleSort} />
                          <SortHeader label="Item #" sortKey="item_number" current={lineItemSort} onSort={handleSort} className="w-24" />
                          <SortHeader label="Qty" sortKey="quantity_ordered" current={lineItemSort} onSort={handleSort} align="right" className="w-16" />
                          <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium w-14">UOM</th>
                          <SortHeader label="Unit Price" sortKey="unit_price" current={lineItemSort} onSort={handleSort} align="right" className="w-24" />
                          <SortHeader label="Extended" sortKey="extended_amount" current={lineItemSort} onSort={handleSort} align="right" className="w-24 pr-5" />
                          {enrichedItems.length > 0 && (
                            <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Resolved Name</th>
                          )}
                          {enrichedItems.length > 0 && (
                            <th className="text-left py-2 px-3 text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Price Breakdown</th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedLineItems.map((li, i) => {
                          const enriched = enrichmentMap[li.id];
                          const identity = enriched?.enrichment?.identity?.required || {};
                          const pricing = enriched?.enrichment?.pricing?.required || {};
                          const priceVector = pricing.price_vector || [];

                          return (
                            <tr key={li.id || i} className={`border-b border-zinc-50 ${i % 2 === 1 ? 'bg-zinc-50/30' : ''} hover:bg-zinc-50`}>

                                <td className="py-2 px-3 pl-5 text-zinc-400 tabular-nums">{li.line_number || i + 1}</td>
                                <td className="py-2 px-3 text-zinc-700 max-w-xs truncate">{li.description}</td>
                                <td className="py-2 px-3 text-zinc-400 font-mono text-[11px]">{li.item_number || '--'}</td>
                                <td className="py-2 px-3 text-right text-zinc-600 tabular-nums">{li.quantity_ordered ?? li.quantity_shipped ?? '--'}</td>
                                <td className="py-2 px-3 text-zinc-500">{li.unit_of_measure || '--'}</td>
                                <td className="py-2 px-3 text-right text-zinc-600 tabular-nums">{fmt(li.unit_price) || '--'}</td>
                                <td className="py-2 px-3 pr-5 text-right text-zinc-800 font-medium tabular-nums">{fmt(li.extended_amount) || '--'}</td>
                                {enrichedItems.length > 0 && (
                                  <td className="py-2 px-3">
                                    {identity.standardized_name ? (
                                      <div className="min-w-0">
                                        <span className="text-xs text-zinc-700 font-medium truncate block">{identity.standardized_name}</span>
                                        {identity.category && (
                                          <span className="text-[10px] text-zinc-400 block">{identity.category}{identity.subcategory ? ` > ${identity.subcategory}` : ''}</span>
                                        )}
                                      </div>
                                    ) : (
                                      <span className="text-zinc-300">--</span>
                                    )}
                                  </td>
                                )}
                                {enrichedItems.length > 0 && (
                                  <td className="py-2 px-3">
                                    {priceVector.length > 0 ? (
                                      <div className="space-y-0.5">
                                        {priceVector.map((pv, j) => (
                                          <div key={j} className={`text-[11px] tabular-nums ${pv.is_primary ? 'text-emerald-700 font-semibold' : 'text-zinc-500'}`}>
                                            {fmt(pv.price)} <span className="font-normal">{pv.label}</span>
                                          </div>
                                        ))}
                                      </div>
                                    ) : (
                                      <span className="text-zinc-300">--</span>
                                    )}
                                  </td>
                                )}
                              </tr>
                          );
                        })}
                      </tbody>
                      {/* Totals row */}
                      <tfoot>
                        <tr className="border-t border-zinc-200 bg-zinc-50/50">
                          <td colSpan={5} className="py-2 px-3 pl-5" />
                          <td className="py-2 px-3 text-right text-[10px] uppercase tracking-wider text-zinc-400 font-medium">Total</td>
                          <td className="py-2 px-3 pr-5 text-right text-sm font-semibold text-zinc-900 tabular-nums">
                            {fmt(totalAmount)}
                          </td>
                          {enrichedItems.length > 0 && <td colSpan={2} />}
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}

              {/* ─── Enrichment Highlights ─── */}
              {enrichedItems.length > 0 && (
                <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-100">
                    <div className="flex items-center gap-3">
                      <h3 className="text-sm font-semibold text-zinc-900">Product Resolution</h3>
                      {normalizedCount > 0 && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">
                          {normalizedCount} with unit pricing
                        </span>
                      )}
                    </div>
                    <span className="text-[11px] text-zinc-400">{enrichedItems.length} resolved</span>
                  </div>
                  <div className="divide-y divide-zinc-100">
                    {enrichedItems.map((item) => {
                      const identity = item.enrichment?.identity?.required || {};
                      const pricing = item.enrichment?.pricing?.required || {};
                      const raw = item.raw || {};
                      const conf = item.enrichment?.identity?.supplementary?._confidence;

                      return (
                        <div key={item.line_item_id} className="px-5 py-3">
                          <div className="flex items-center gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-zinc-800 truncate">
                                  {identity.standardized_name || identity.canonical_name?.replace(/_/g, ' ') || raw.description || '--'}
                                </span>
                                {conf != null && (
                                  <span className={`px-1.5 py-0.5 text-[9px] font-semibold rounded ${
                                    conf >= 0.85 ? 'bg-emerald-100 text-emerald-700' :
                                    conf >= 0.7 ? 'bg-amber-100 text-amber-700' :
                                    'bg-red-100 text-red-700'
                                  }`}>
                                    {(conf * 100).toFixed(0)}%
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-0.5 text-[11px] text-zinc-400">
                                {identity.category && <span>{identity.category}{identity.subcategory ? ` > ${identity.subcategory}` : ''}</span>}
                                {identity.brand && <span>Brand: {identity.brand}</span>}
                              </div>
                            </div>
                            <div className="text-right shrink-0">
                              <div className="text-xs text-zinc-500">
                                {raw.unit_of_measure} @ {fmt(raw.unit_price)}
                                <span className="text-zinc-300 ml-1">(package)</span>
                              </div>
                              {pricing.price_per_unit && pricing.unit_normalized && (
                                <div className="text-xs font-medium mt-0.5 text-emerald-600">
                                  {fmt(pricing.price_per_unit)}/{pricing.unit_normalized}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ─── Additional Details (collapsible) ─── */}
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setShowRawData(!showRawData)}
                  className="w-full flex items-center justify-between px-5 py-3 hover:bg-zinc-50 transition-colors text-left"
                >
                  <h3 className="text-sm font-semibold text-zinc-900">All Extracted Fields</h3>
                  <div className="flex items-center gap-2">
                    <span className="text-[11px] text-zinc-400">{Object.keys(header).length} fields</span>
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"
                      className={`text-zinc-400 transition-transform ${showRawData ? 'rotate-180' : ''}`}>
                      <path d="M2 4l4 4 4-4" />
                    </svg>
                  </div>
                </button>
                {showRawData && (
                  <div className="border-t border-zinc-100 px-5 py-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {Object.entries(header).map(([k, v]) => (
                        <div key={k}>
                          <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium mb-0.5">{k.replace(/_/g, ' ')}</p>
                          <p className="text-xs text-zinc-700 truncate">{String(v)}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" className="text-zinc-200 mb-3">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8L14 2Z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h4" />
              </svg>
              <p className="text-sm text-zinc-400">Select a document to view details</p>
            </div>
          )}
        </div>

        {/* PDF viewer panel */}
        {pdfOpen && pdfUrl && (
          <div className="w-[480px] shrink-0 flex flex-col border border-zinc-200 rounded-xl overflow-hidden bg-white sticky top-0 self-start" style={{ height: 'calc(100vh - 140px)' }}>
            <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-100 bg-zinc-50/50">
              <span className="text-xs font-medium text-zinc-600">{selectedDoc?.file_name || 'Document'}</span>
              <button onClick={() => setPdfOpen(false)} className="text-zinc-400 hover:text-zinc-600 transition-colors">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M4 4l6 6M10 4l-6 6" /></svg>
              </button>
            </div>
            <iframe src={pdfUrl} title="Document PDF" className="flex-1 w-full" style={{ border: 'none' }} />
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Sub-components ───

const SummaryField = ({ label, value, large }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wider text-zinc-400 font-medium mb-0.5">{label}</p>
    <p className={`${large ? 'text-lg font-semibold' : 'text-sm font-medium'} text-zinc-900 truncate`}>
      {value || <span className="text-zinc-300">--</span>}
    </p>
  </div>
);

const SortHeader = ({ label, sortKey, current, onSort, align = 'left', className = '' }) => {
  const isActive = current.key === sortKey;
  return (
    <th
      onClick={() => onSort(sortKey)}
      className={`py-2 px-3 text-[10px] uppercase tracking-wider font-medium cursor-pointer select-none transition-colors hover:text-zinc-600 ${
        align === 'right' ? 'text-right' : 'text-left'
      } ${isActive ? 'text-zinc-700' : 'text-zinc-400'} ${className}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {isActive && (
          <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor" className={current.dir === 'desc' ? 'rotate-180' : ''}>
            <path d="M4 1l3 5H1z" />
          </svg>
        )}
      </span>
    </th>
  );
};

export default DocumentDetailView;
